import { TextComponent } from 'baseComponents/TextComponent';
import { AdminPageWrapper } from 'components/layouts/AdminPageWrapper';
import { Flex, notification, Spin } from 'antd';
import { AdminApiConfigForm } from 'components/AdminApiConfigForm';
import { useGetApiConfiguration } from 'hooks/useGetApiConfiguration';
import { FullScreenLoaderComponent } from 'baseComponents/FullScreenLoaderComponent';
import { useSetApiConfiguration } from 'hooks/useSetApiConfiguration';
import './styles.scss';
import { AdminErrorPage } from 'components/AdminErrorPage';
import { useEffect, useState } from 'react';
import { useGenerateApiKey } from '../../../hooks/useGenerateApiKey';
import { AdminGenerateApiKeyForm } from '../../../components/AdminGenerateApiKeyForm';

export const AdminApiConfiguration = () => {
  const [api, contextHolder] = notification.useNotification();
  const { data, isLoading, refetch, isRefetching } = useGetApiConfiguration();
  const [apiKey, setApiKey] = useState<string | null>(null);

  const {
    mutate: setBrazeApiKey,
    isPending,
    isError,
  } = useSetApiConfiguration({
    onSuccess: () => {
      refetch();
      api.success({
        message: `Api key updated`,
        description: 'Braze api key has been successfully updated',
        duration: 3,
      });
    },
    onError: (e) => {
      api.error({
        message: 'Something went wrong',
        description:
          e.message ||
          'Something went wrong with updating Braze api key. Please try again later. ',
        duration: 3,
      });
    },
  });

  useEffect(() => {
    if (data) {
      setApiKey(data.api_key);
    }
  }, [data]);

  const { mutate: generateKey, isPending: isGenerateKeyPending } =
    useGenerateApiKey({
      onSuccess: (response) => {
        setApiKey(response.api_key);

        api.success({
          message: `Api key updated`,
          description: 'Api key has been successfully generated',
          duration: 3,
        });
      },
      onError: (e) => {
        api.error({
          message: 'Something went wrong',
          description:
            e.message ||
            'Something went wrong with generating api key. Please try again later.',
          duration: 3,
        });
      },
    });

  if (isError) {
    return (
      <AdminErrorPage
        isRefetching={isRefetching}
        refetch={refetch}
        error={'Error fetching API configuration'}
      />
    );
  }

  const copyToClipboard = () => {
    if (apiKey) {
      navigator.clipboard.writeText(apiKey).then(() => {
        api.success({ message: 'Copied to clipboard' });
      });
    }
  };

  return (
    <AdminPageWrapper sidebar={<div style={{ width: 160 }} />}>
      {contextHolder}
      <TextComponent size='table-title-size' text='API Configuration' />
      <TextComponent text='Please ensure the key has the following permissions: ' />
      <ul className='config-list'>
        <li>
          <TextComponent size='middle' text='User Data' />
        </li>
        <li>
          <TextComponent size='middle' text='Campaigns' />
        </li>
        <li>
          <TextComponent size='middle' text='Messages' />
        </li>
      </ul>
      {isLoading ? (
        <Flex justify='center' align='center' style={{ height: 200 }}>
          <Spin size='large' />
        </Flex>
      ) : (
        <div>
          <AdminApiConfigForm
            submitButtonLabel='Submit'
            onSubmit={(data) => {
              setBrazeApiKey({
                braze_api_key: data.apiKey,
              });
            }}
            defaultValues={{
              apiKey: data?.braze_api_key || '',
            }}
          />
          <AdminGenerateApiKeyForm
            apiKeyValue={apiKey}
            onGenerateButtonClick={generateKey}
            onCopyToClipboardButtonClick={copyToClipboard}
          />
        </div>
      )}
      <FullScreenLoaderComponent
        spinning={isLoading || isPending || isGenerateKeyPending}
      />
    </AdminPageWrapper>
  );
};
